import { fetchGraphQL, getAuthHeader } from "./api"
import type {
  Photo,
  CreatePhotoInput,
  UpdatePhotoInput,
  DeleteResponse,
  QueryPhotoArgs,
  QueryPhotosArgs,
  MutationCreatePhotoArgs,
  MutationDeletePhotoArgs,
  MutationUpdatePhotoArgs,
} from "../graphql/schema"

const PHOTO_FRAGMENT = `
  fragment PhotoFields on Photo {
    slug
    title
    altText
    description
    fileId
    status
    authorId
    gallerySlug
    width
    height
    dateTaken
    dateUploaded
    locationData
    exifData
  }
`

const GET_PHOTOS_QUERY = `
  ${PHOTO_FRAGMENT}
  query GetPhotos($limit: Int, $offset: Int, $status: String) {
    photos(limit: $limit, offset: $offset, status: $status) {
      ...PhotoFields
    }
  }
`

const GET_PHOTO_QUERY = `
  ${PHOTO_FRAGMENT}
  query GetPhoto($slug: String!) {
    photo(slug: $slug) {
      ...PhotoFields
    }
  }
`

const CREATE_PHOTO_MUTATION = `
  ${PHOTO_FRAGMENT}
  mutation CreatePhoto($input: CreatePhotoInput!) {
    createPhoto(input: $input) {
      ...PhotoFields
    }
  }
`

const UPDATE_PHOTO_MUTATION = `
  ${PHOTO_FRAGMENT}
  mutation UpdatePhoto($slug: String!, $input: UpdatePhotoInput!) {
    updatePhoto(slug: $slug, input: $input) {
      ...PhotoFields
    }
  }
`

const DELETE_PHOTO_MUTATION = `
  mutation DeletePhoto($slug: String!) {
    deletePhoto(slug: $slug) {
      slug
      success
    }
  }
`

// ==========================================
// Photo Service Implementation
// ==========================================

/**
 * 获取照片列表
 */
export async function getPhotos(
  params: QueryPhotosArgs = { limit: 10, offset: 0 },
  headers: Record<string, string> = {}
): Promise<Photo[]> {
  type GetPhotosResponse = {
    photos: Photo[]
  }

  const data = await fetchGraphQL<GetPhotosResponse, QueryPhotosArgs>(
    GET_PHOTOS_QUERY,
    headers,
    params
  )

  return data.photos || []
}

/**
 * 获取照片详情
 * @param slug 照片的 slug 标识
 */
export async function getPhoto(slug: string): Promise<Photo | null> {
  type GetPhotoResponse = {
    photo: Photo | null
  }

  const data = await fetchGraphQL<GetPhotoResponse, QueryPhotoArgs>(
    GET_PHOTO_QUERY,
    {},
    { slug }
  )

  return data.photo || null
}

/**
 * 创建新照片
 * @param input 创建参数
 */
export async function createPhoto(
  input: CreatePhotoInput
): Promise<Photo | null> {
  type CreatePhotoResponse = {
    createPhoto: Photo | null
  }

  const data = await fetchGraphQL<CreatePhotoResponse, MutationCreatePhotoArgs>(
    CREATE_PHOTO_MUTATION,
    getAuthHeader(),
    { input }
  )

  return data.createPhoto || null
}

/**
 * 更新照片信息
 * @param slug 目标照片 slug
 * @param input 更新参数（只需传入需要修改的字段）
 */
export async function updatePhoto(
  slug: string,
  input: UpdatePhotoInput
): Promise<Photo | null> {
  type UpdatePhotoResponse = {
    updatePhoto: Photo | null
  }

  const data = await fetchGraphQL<UpdatePhotoResponse, MutationUpdatePhotoArgs>(
    UPDATE_PHOTO_MUTATION,
    getAuthHeader(),
    { slug, input }
  )

  return data.updatePhoto || null
}

/**
 * 删除照片
 * @param slug 目标照片 slug
 * @returns DeleteResponse (包含 success 状态和被删除的 slug)
 */
export async function deletePhoto(
  slug: string,
  headers: Record<string, string>
): Promise<DeleteResponse> {
  type DeletePhotoResponse = {
    deletePhoto: DeleteResponse
  }

  const data = await fetchGraphQL<DeletePhotoResponse, MutationDeletePhotoArgs>(
    DELETE_PHOTO_MUTATION,
    headers,
    { slug }
  )

  return data.deletePhoto || { success: false, slug: null }
}
